import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { blogAPI, uploadAPI } from '../../utils/api';
import { Plus, Edit2, Trash2, X, Check, MessageSquare, Eye, Upload, Bold, Italic, List, Link as LinkIcon, Image as ImageIcon, AlignLeft } from 'lucide-react';
import toast from 'react-hot-toast';

const EMPTY_FORM = { title:'', excerpt:'', cover_image:'', content:'', tags:'', status:'published' };

// Rich Text Editor component
function RichEditor({ value, onChange }) {
  const editorRef = useRef();
  const fileRef = useRef();

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, []);

  const exec = (cmd, val = null) => {
    editorRef.current.focus();
    document.execCommand(cmd, false, val);
    onChange(editorRef.current.innerHTML);
  };

  const insertLink = () => {
    const url = window.prompt('URL-i i linkut:');
    if (url) exec('createLink', url);
  };

  const insertImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const { data } = await uploadAPI.image(file);
      exec('insertImage', data.url);
      toast.success('Imazhi u ngarkua!');
    } catch { toast.error('Gabim gjatë ngarkimit'); }
    e.target.value = '';
  };

  const insertVideo = () => {
    const url = window.prompt('URL-i i videos (YouTube embed ose direct):');
    if (!url) return;
    let html = '';
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const id = url.match(/(?:v=|youtu\.be\/)([^&?/]+)/)?.[1];
      if (id) html = `<div style="position:relative;padding-bottom:56.25%;height:0;margin:16px 0"><iframe src="https://www.youtube.com/embed/${id}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0" allowfullscreen></iframe></div>`;
    } else {
      html = `<video controls style="width:100%;max-width:100%;margin:16px 0"><source src="${url}" /></video>`;
    }
    if (html) {
      exec('insertHTML', html);
    }
  };

  const TOOLS = [
    { icon: <Bold size={14}/>, cmd: () => exec('bold'), title:'Bold' },
    { icon: <Italic size={14}/>, cmd: () => exec('italic'), title:'Italic' },
    { icon: 'H2', cmd: () => exec('formatBlock','h2'), title:'Titull H2' },
    { icon: 'H3', cmd: () => exec('formatBlock','h3'), title:'Titull H3' },
    { icon: <List size={14}/>, cmd: () => exec('insertUnorderedList'), title:'Lista' },
    { icon: <AlignLeft size={14}/>, cmd: () => exec('formatBlock','p'), title:'Paragraf' },
    { icon: <LinkIcon size={14}/>, cmd: insertLink, title:'Link' },
    { icon: <ImageIcon size={14}/>, cmd: () => fileRef.current.click(), title:'Ngarko Imazh' },
    { icon: '▶', cmd: insertVideo, title:'Video (YouTube/MP4)' },
    { icon: '—', cmd: () => exec('insertHorizontalRule'), title:'Vijë ndarëse' },
    { icon: '<>', cmd: () => exec('formatBlock','pre'), title:'Kod' },
  ];

  return (
    <div style={{ border:'1.5px solid var(--cream-dark)', borderRadius:'var(--radius-md)', overflow:'hidden' }}>
      <input ref={fileRef} type="file" accept="image/*" style={{ display:'none' }} onChange={insertImage} />
      {/* Toolbar */}
      <div style={{ display:'flex', gap:2, padding:'8px 10px', background:'var(--cream)', borderBottom:'1px solid var(--cream-dark)', flexWrap:'wrap' }}>
        {TOOLS.map((t, i) => (
          <button key={i} type="button" title={t.title} onMouseDown={e => { e.preventDefault(); t.cmd(); }}
            style={{ padding:'5px 9px', border:'1px solid var(--cream-dark)', borderRadius:6, background:'white', cursor:'pointer', fontSize:'.78rem', color:'var(--green-deep)', display:'flex', alignItems:'center', justifyContent:'center', minWidth:32, transition:'all .15s' }}
            onMouseEnter={e => e.target.style.background='var(--green-soft)'}
            onMouseLeave={e => e.target.style.background='white'}
          >{t.icon}</button>
        ))}
      </div>
      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(editorRef.current.innerHTML)}
        style={{ minHeight:280, padding:'16px', outline:'none', fontSize:'.95rem', lineHeight:1.7, color:'var(--text-dark)' }}
        data-placeholder="Shkruaj permbajtjen e artikullit ketu..."
      />
      <style>{`
        [contenteditable]:empty:before { content: attr(data-placeholder); color: var(--text-light); }
        [contenteditable] h2 { font-size:1.4rem; margin:20px 0 10px; }
        [contenteditable] h3 { font-size:1.15rem; margin:16px 0 8px; }
        [contenteditable] ul { padding-left:20px; margin:8px 0; }
        [contenteditable] a { color:var(--green-mid); text-decoration:underline; }
        [contenteditable] img { max-width:100%; border-radius:8px; margin:8px 0; }
        [contenteditable] pre { background:var(--cream); padding:12px; border-radius:8px; font-family:monospace; overflow-x:auto; }
        [contenteditable] hr { border:none; border-top:2px solid var(--cream-dark); margin:20px 0; }
      `}</style>
    </div>
  );
}

export default function AdminBlogPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [tab, setTab] = useState('posts');

  const { data: posts = [], isLoading } = useQuery({ queryKey:['admin-blog-posts'], queryFn: () => blogAPI.adminGetAll().then(r => r.data) });
  const { data: comments = [], isLoading: commentsLoading } = useQuery({ queryKey:['admin-comments'], queryFn: () => blogAPI.getComments().then(r => r.data) });

  const pendingComments = comments.filter(c => !c.is_approved);
  const approvedComments = comments.filter(c => c.is_approved);

  const saveMut = useMutation({
    mutationFn: (p) => editing ? blogAPI.update(editing.id, p) : blogAPI.create(p),
    onSuccess: () => { toast.success(editing ? 'Artikulli u përditësua!' : 'Artikulli u krijua!'); qc.invalidateQueries(['admin-blog-posts']); setShowForm(false); setEditing(null); setForm(EMPTY_FORM); },
    onError: (e) => toast.error(e.response?.data?.error || 'Gabim'),
  });
  const deleteMut = useMutation({
    mutationFn: blogAPI.remove,
    onSuccess: () => { toast.success('Artikulli u fshi'); qc.invalidateQueries(['admin-blog-posts']); },
  });
  const approveCmtMut = useMutation({
    mutationFn: blogAPI.approveComment,
    onSuccess: () => { toast.success('Komenti u miratua!'); qc.invalidateQueries(['admin-comments']); },
  });
  const deleteCmtMut = useMutation({
    mutationFn: blogAPI.deleteComment,
    onSuccess: () => { toast.success('Komenti u fshi'); qc.invalidateQueries(['admin-comments']); },
  });

  const openEdit = (post) => {
    setEditing(post);
    setForm({
      title: post.title || '',
      excerpt: post.excerpt || '',
      cover_image: post.cover_image || '',
      content: post.content || '',
      tags: Array.isArray(post.tags) ? post.tags.join(', ') : (post.tags ? (() => { try { return JSON.parse(post.tags).join(', '); } catch { return post.tags; } })() : ''),
      status: post.status || 'draft',
    });
    setShowForm(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    saveMut.mutate({ ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) });
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24, flexWrap:'wrap', gap:16 }}>
        <div>
          <h1 style={{ fontSize:'1.6rem', marginBottom:4 }}>Blog</h1>
          <p style={{ color:'var(--text-light)', fontSize:'.88rem' }}>{posts.length} artikuj · {pendingComments.length} komente në pritje</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditing(null); setForm(EMPTY_FORM); setShowForm(true); }}>
          <Plus size={16} /> Artikull i Ri
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, background:'white', borderRadius:'var(--radius-md)', padding:4, border:'1px solid var(--cream-dark)', width:'fit-content', marginBottom:20 }}>
        {[['posts','Artikujt'],['pending',`Komente në Pritje (${pendingComments.length})`],['approved','Komente të Miratuara']].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ padding:'7px 16px', borderRadius:'var(--radius-sm)', border:'none', cursor:'pointer', fontSize:'.82rem', fontWeight:500, background:tab===k?'var(--green-deep)':'transparent', color:tab===k?'white':'var(--text-light)', transition:'all .2s', position:'relative' }}>{l}</button>
        ))}
      </div>

      {/* POSTS */}
      {tab === 'posts' && (
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Titulli</th><th>Autori</th><th>Komente</th><th>Statusi</th><th>Publikimi</th><th>Veprime</th></tr></thead>
              <tbody>
                {isLoading && <tr><td colSpan={6} style={{ textAlign:'center', padding:40 }}><div className="spinner" style={{ margin:'0 auto' }} /></td></tr>}
                {!isLoading && posts.length===0 && <tr><td colSpan={6} style={{ textAlign:'center', padding:40, color:'var(--text-light)' }}>Nuk ka artikuj ende.</td></tr>}
                {posts.map(post => (
                  <tr key={post.id}>
                    <td style={{ minWidth:220 }}>
                      <div style={{ fontWeight:600, fontSize:'.9rem' }}>{post.title}</div>
                      <div style={{ fontSize:'.78rem', color:'var(--text-light)' }}>{post.excerpt?.slice(0,70)}...</div>
                    </td>
                    <td>{post.author_name || 'Admin'}</td>
                    <td><span className={`badge ${post.comment_count>0?'badge-blue':'badge-gray'}`}>{post.comment_count||0}</span></td>
                    <td><span className={`badge ${post.status==='published'?'badge-green':'badge-gray'}`}>{post.status==='published'?'Publikuar':'Draft'}</span></td>
                    <td style={{ fontSize:'.82rem' }}>{post.published_at ? new Date(post.published_at).toLocaleDateString('sq') : '-'}</td>
                    <td><div style={{ display:'flex', gap:6 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => openEdit(post)}><Edit2 size={14} /></button>
                      <button className="btn btn-ghost btn-sm" style={{ color:'var(--danger)' }} onClick={() => { if(window.confirm('Fshi artikullin?')) deleteMut.mutate(post.id); }}><Trash2 size={14} /></button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* PENDING COMMENTS */}
      {tab === 'pending' && (
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {commentsLoading && <div className="spinner" style={{ margin:'40px auto' }} />}
          {!commentsLoading && pendingComments.length===0 && (
            <div style={{ textAlign:'center', padding:60, background:'white', borderRadius:'var(--radius-xl)', color:'var(--text-light)' }}>
              <MessageSquare size={40} strokeWidth={1} style={{ margin:'0 auto 12px' }} />
              <p>Nuk ka komente në pritje.</p>
            </div>
          )}
          {pendingComments.map(c => (
            <div key={c.id} className="card" style={{ padding:18 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:'.75rem', color:'var(--text-light)', marginBottom:6 }}>
                    <strong>{c.user_name || c.guest_name || 'Guest'}</strong> · Artikulli: <em>{c.post_title}</em> · {new Date(c.created_at).toLocaleDateString('sq')}
                  </div>
                  <p style={{ fontSize:'.9rem', color:'var(--text-dark)', lineHeight:1.6 }}>{c.body}</p>
                </div>
                <div style={{ display:'flex', gap:8, flexShrink:0 }}>
                  <button className="btn btn-primary btn-sm" onClick={() => approveCmtMut.mutate(c.id)} disabled={approveCmtMut.isPending}>
                    <Check size={14} /> Mirafo
                  </button>
                  <button className="btn btn-ghost btn-sm" style={{ color:'var(--danger)' }} onClick={() => { if(window.confirm('Fshi komentin?')) deleteCmtMut.mutate(c.id); }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* APPROVED COMMENTS */}
      {tab === 'approved' && (
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {commentsLoading && <div className="spinner" style={{ margin:'40px auto' }} />}
          {!commentsLoading && approvedComments.length===0 && (
            <div style={{ textAlign:'center', padding:60, background:'white', borderRadius:'var(--radius-xl)', color:'var(--text-light)' }}>Nuk ka komente të miratuara.</div>
          )}
          {approvedComments.map(c => (
            <div key={c.id} className="card" style={{ padding:18 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:'.75rem', color:'var(--text-light)', marginBottom:6 }}>
                    <strong>{c.user_name || c.guest_name || 'Guest'}</strong> · <em>{c.post_title}</em> · {new Date(c.created_at).toLocaleDateString('sq')}
                  </div>
                  <p style={{ fontSize:'.9rem', color:'var(--text-dark)', lineHeight:1.6 }}>{c.body}</p>
                </div>
                <button className="btn btn-ghost btn-sm" style={{ color:'var(--danger)' }} onClick={() => { if(window.confirm('Fshi komentin?')) deleteCmtMut.mutate(c.id); }}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Post Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setShowForm(false)}>
          <div className="modal-box" style={{ maxWidth:860 }}>
            <div className="modal-header">
              <h2 style={{ fontSize:'1.2rem' }}>{editing ? 'Ndrysho Artikull' : 'Artikull i Ri'}</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowForm(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleSubmit} className="modal-body">
              <div className="form-group"><label className="form-label">Titulli *</label>
                <input className="form-input" required value={form.title} onChange={e => setForm({...form,title:e.target.value})} />
              </div>
              <div className="form-group"><label className="form-label">Përmbledhje *</label>
                <textarea className="form-input" rows={2} required value={form.excerpt} onChange={e => setForm({...form,excerpt:e.target.value})} />
              </div>
              <div className="form-group"><label className="form-label">Foto Mbulesë (URL)</label>
                <input className="form-input" value={form.cover_image} onChange={e => setForm({...form,cover_image:e.target.value})} placeholder="https://..." />
              </div>
              <div className="form-group">
                <label className="form-label">Përmbajtja — Mbështet tekst, tituj, lista, imazhe, video YouTube/MP4, linqe dhe kod</label>
                <RichEditor value={form.content} onChange={v => setForm({...form,content:v})} />
              </div>
              <div className="form-row-2">
                <div className="form-group"><label className="form-label">Etiketat (ndaj me presje)</label>
                  <input className="form-input" value={form.tags} onChange={e => setForm({...form,tags:e.target.value})} placeholder="flokë, natyral, kujdes" />
                </div>
                <div className="form-group"><label className="form-label">Statusi</label>
                  <select className="form-input" value={form.status} onChange={e => setForm({...form,status:e.target.value})}>
                    <option value="draft">Draft</option>
                    <option value="published">Publikuar</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)}>Anulo</button>
                <button type="submit" className="btn btn-primary" disabled={saveMut.isPending}>
                  {saveMut.isPending ? 'Duke ruajtur...' : editing ? 'Ruaj Ndryshimet' : 'Krijo Artikull'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .card { background:white;border-radius:var(--radius-xl);border:1px solid var(--cream-dark); }
        .modal-overlay { position:fixed;inset:0;background:rgba(0,0,0,.5);z-index:2000;display:flex;align-items:center;justify-content:center;padding:20px;backdrop-filter:blur(4px); }
        .modal-box { background:white;border-radius:var(--radius-xl);width:min(860px,100%);max-height:92vh;display:flex;flex-direction:column;animation:modalIn .2s ease; }
        @keyframes modalIn { from{opacity:0;transform:scale(.96)} to{opacity:1;transform:scale(1)} }
        .modal-header { display:flex;justify-content:space-between;align-items:center;padding:20px 24px;border-bottom:1px solid var(--cream-dark); }
        .modal-body { padding:24px;overflow-y:auto;flex:1; }
        .modal-footer { display:flex;justify-content:flex-end;gap:12px;padding-top:16px;border-top:1px solid var(--cream-dark);margin-top:8px; }
        .form-row-2 { display:grid;grid-template-columns:1fr 1fr;gap:16px; }
      `}</style>
    </div>
  );
}
